import { useNavigate, useParams } from "react-router-dom";
import { Check, LayoutDashboard, LogIn } from "lucide-react";
import { PageShell } from "@/components/common/PageShell";
import { SUPPORT_EMAIL } from "@/components/common/SupportLink";
import FeedbackForm from "@/components/FeedbackForm";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useInterviewInfo } from "@/hooks/useInterview";
import { INTERVIEWER } from "@/lib/roles";

/**
 * After the call, a card after Cal.com's confirmation page. Everyone can
 * rejoin; the interviewer also gets their dashboard and, below, the feedback
 * form (not on partner interviews, where the partner interviews its own
 * candidate).
 */
const PostLeave = () => {
  const navigate = useNavigate();
  const { roomId, role } = useParams();
  const isInterviewer = role === INTERVIEWER;
  const info = useInterviewInfo(roomId);
  const canGiveFeedback =
    isInterviewer && info.status === "ready" && !info.data?.partner_interview;
  const projectName = info.data?.project?.name;

  return (
    <PageShell>
      <Card className="w-full max-w-lg gap-0 py-0" data-testid="leave_card">
        <div className="flex flex-col items-center gap-4 px-6 pt-8 pb-6 text-center">
          <div className="flex size-11 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
            <Check className="size-5" />
          </div>
          <div className="flex flex-col gap-1">
            <h1 className="text-lg font-semibold tracking-tight">
              You left the interview
            </h1>
            <p className="text-sm text-muted-foreground">
              {projectName
                ? `Interview for ${projectName}.`
                : "You can join again while the interview is on."}
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            <Button
              variant={canGiveFeedback ? "outline" : "default"}
              data-testid="join_again_btn"
              onClick={() => navigate(`/preview/${roomId}/${role}`)}
            >
              <LogIn />
              Join again
            </Button>
            {isInterviewer ? (
              <Button
                variant="outline"
                data-testid="go_to_dashboard_btn"
                onClick={() =>
                  window.open(
                    process.env.REACT_APP_SITE,
                    "_blank",
                    "noopener,noreferrer"
                  )
                }
              >
                <LayoutDashboard />
                Your dashboard
              </Button>
            ) : null}
          </div>
        </div>
        {canGiveFeedback ? (
          <div className="border-t px-6 py-6">
            <FeedbackForm interviewInfo={info.data} />
          </div>
        ) : null}
        <p className="border-t bg-muted/50 px-6 py-3 text-center text-sm text-muted-foreground">
          Need help?{" "}
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            data-testid="contact_us_btn"
            className="font-medium text-brand underline-offset-4 hover:underline"
          >
            Contact us
          </a>
        </p>
      </Card>
    </PageShell>
  );
};

export default PostLeave;
